import { useState, useEffect, useCallback } from "react";
import { listCargoServices, createCargoService, updateCargoService, deleteCargoService, copyCargoService, copyCargoServicesToYear } from "@/lib/api";
import { Wrench, Plus, Pencil, Trash2, Search, Star, ArrowLeftRight, CalendarDays, CopyPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const PARTIES = [
  { key: "cargo_agency", label: "Cargo Agency" },
  { key: "airline", label: "Airline" },
];
const THIS_YEAR = new Date().getFullYear();
const empty = { name: "", category: "", unit: "", price: "", min_charge: "", currency: "EUR", validity: "", notes: "", is_active: true, party: "cargo_agency" };
const priceStr = (v) => (v === "" || v === null || v === undefined) ? "—" : `€ ${v}`;

export default function CargoServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [party, setParty] = useState("cargo_agency");
  const [year, setYear] = useState(THIS_YEAR);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyPct, setCopyPct] = useState("0");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCargoServices(year);
      const rows = (res.data || []).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setItems(rows);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => { setForm({ ...empty, party, year }); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Service name is required"); return; }
    try {
      if (editId) { await updateCargoService(editId, form); toast.success("Service updated"); }
      else { await createCargoService(form); toast.success("Service created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save service"); }
  };

  const confirmDelete = async () => {
    try { await deleteCargoService(deleteId); toast.success("Service deleted"); setDeleteId(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const toggleFav = async (it) => {
    const next = !it.is_favorite;
    setItems((p) => p.map((x) => (x.id === it.id ? { ...x, is_favorite: next } : x)));
    try { await updateCargoService(it.id, { ...it, is_favorite: next }); }
    catch { toast.error("Could not update favourite"); load(); }
  };

  const transfer = async (it) => {
    const target = (it.party || "cargo_agency") === "cargo_agency" ? "airline" : "cargo_agency";
    const targetLabel = target === "airline" ? "Airline" : "Cargo Agency";
    try {
      await copyCargoService(it.id, target);
      toast.success(`"${it.name}" copied to ${targetLabel}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to transfer"); }
  };

  const runCopyToNextYear = async () => {
    const pct = parseFloat(copyPct || "0");
    try {
      const r = await copyCargoServicesToYear(year, year + 1, isNaN(pct) ? 0 : pct);
      toast.success(`${r.data.created} services copied to ${year + 1} (+${r.data.percent}%)`);
      setCopyOpen(false); setYear(year + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to copy prices"); }
  };

  const filtered = (q
    ? items.filter((it) => `${it.name} ${it.category} ${it.notes}`.toLowerCase().includes(q.toLowerCase()))
    : items
  ).filter((it) => (it.party || "cargo_agency") === party);

  const grouped = filtered.reduce((acc, it) => {
    const c = it.category || "Uncategorised";
    (acc[c] = acc[c] || []).push(it);
    return acc;
  }, {});

  const yearOptions = Array.from(new Set([THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1, THIS_YEAR + 2, THIS_YEAR + 3, year])).sort((a, b) => a - b);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-services-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="text-emerald-500" /> Services & Rates
          </h1>
          <p className="text-slate-500 text-sm mt-1">BSCA Cargo charges — prices for the selected year, grouped by category.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1" data-testid="service-year-picker">
            <CalendarDays size={16} className="text-slate-400" />
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-transparent text-sm font-semibold text-slate-800 focus:outline-none" data-testid="service-year-select">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Button variant="outline" onClick={() => { setCopyPct("0"); setCopyOpen(true); }} data-testid="copy-year-btn" title={`Copy ${year} prices to ${year + 1}`}>
            <CopyPlus size={16} className="mr-2" /> Copy to {year + 1}
          </Button>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-service-btn">
            <Plus size={18} className="mr-2" /> New Service
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search a service or category…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="service-search" />
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="service-party-tabs">
        {PARTIES.map((p) => (
          <button
            key={p.key}
            onClick={() => setParty(p.key)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${party === p.key ? (p.key === "airline" ? "bg-blue-600 text-white shadow-sm" : "bg-emerald-600 text-white shadow-sm") : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`service-party-tab-${p.key}`}
          >
            {p.label} <span className="opacity-70">({items.filter((it) => (it.party || "cargo_agency") === p.key).length})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : Object.keys(grouped).length === 0 ? (
        <p className="text-slate-400 text-sm">No services found.</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, rows]) => (
            <div key={cat} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`service-cat-${cat}`}>
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">{cat}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-2.5 px-4">Service</th>
                      <th className="py-2.5 px-4">Unit</th>
                      <th className="py-2.5 px-4">Price</th>
                      <th className="py-2.5 px-4">Min</th>
                      <th className="py-2.5 px-4">Notes</th>
                      <th className="py-2.5 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((it) => (
                      <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`service-row-${it.id}`}>
                        <td className="py-2.5 px-4 font-medium text-slate-900">
                          <span className="inline-flex items-center gap-1.5">
                            {it.is_favorite && <Star size={13} className="text-amber-500 fill-amber-400" />}
                            {it.name}
                          </span>
                          {!it.is_active && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}
                        </td>
                        <td className="py-2.5 px-4 text-slate-600">{it.unit || "—"}</td>
                        <td className="py-2.5 px-4 font-semibold text-emerald-700 whitespace-nowrap">{priceStr(it.price)}</td>
                        <td className="py-2.5 px-4 text-slate-600 whitespace-nowrap">{it.min_charge ? `€ ${it.min_charge}` : "—"}</td>
                        <td className="py-2.5 px-4 text-slate-500 max-w-[280px]">{it.notes || "—"}</td>
                        <td className="py-2.5 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => toggleFav(it)} className={`h-8 w-8 ${it.is_favorite ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`} data-testid={`fav-service-${it.id}`} title={it.is_favorite ? "Remove from favourites" : "Mark as favourite"}>
                              <Star size={16} className={it.is_favorite ? "fill-amber-400" : ""} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => transfer(it)} className="h-8 w-8 text-slate-500 hover:text-blue-600" data-testid={`transfer-service-${it.id}`} title={`Copy to ${(it.party || "cargo_agency") === "cargo_agency" ? "Airline" : "Cargo Agency"}`}>
                              <ArrowLeftRight size={16} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`edit-service-${it.id}`}><Pencil size={16} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => setDeleteId(it.id)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`delete-service-${it.id}`}><Trash2 size={16} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Service" : "New Service"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Billing party</Label>
              <div className="mt-1 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 w-full sm:w-auto">
                {PARTIES.map((p) => (
                  <button key={p.key} type="button" onClick={() => set("party", p.key)}
                    className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${(form.party || "cargo_agency") === p.key ? (p.key === "airline" ? "bg-blue-600 text-white" : "bg-emerald-600 text-white") : "text-slate-600 hover:text-slate-900"}`}
                    data-testid={`service-form-party-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="service-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1" placeholder="e.g. Screening" data-testid="service-category-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Year</Label>
              <Input type="number" value={form.year ?? year} onChange={(e) => set("year", Number(e.target.value))} className="mt-1" data-testid="service-year-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Price (€)</Label>
              <Input type="number" step="0.0001" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1" data-testid="service-price-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Unit</Label>
              <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} className="mt-1" placeholder="e.g. per kg" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Minimum charge (€)</Label>
              <Input type="number" step="0.01" value={form.min_charge} onChange={(e) => set("min_charge", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Validity</Label>
              <Input value={form.validity} onChange={(e) => set("validity", e.target.value)} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-service-btn">{editId ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent data-testid="copy-year-dialog">
          <DialogHeader><DialogTitle>Copy {year} prices to {year + 1}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-600">Duplicates every {year} service into {year + 1}, increasing each price by the percentage below. Services that already exist for {year + 1} are left unchanged.</p>
            <div>
              <Label className="text-xs text-slate-500">Price increase (%)</Label>
              <Input type="number" step="0.1" value={copyPct} onChange={(e) => setCopyPct(e.target.value)} className="mt-1" data-testid="copy-year-percent-input" placeholder="e.g. 3" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button onClick={runCopyToNextYear} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-copy-year-btn">Copy prices</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this service?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-service-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
